import { useMemo, useState } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  format,
  parseISO,
  isValid,
} from 'date-fns';
import { ChevronLeft, ChevronRight, ShoppingBag, Phone, Mail, Clock } from 'lucide-react';
import { Button } from './ui/button';

const STATUS_META = {
  confirmed: { label: 'Confirmado', color: 'bg-green-500' },
  pending_payment: { label: 'Pendente', color: 'bg-amber-500' },
  preparing: { label: 'Preparando', color: 'bg-blue-500' },
  ready: { label: 'Pronto', color: 'bg-purple-500' },
  delivered: { label: 'Entregue', color: 'bg-stone-500' },
  cancelled: { label: 'Cancelado', color: 'bg-red-500' },
};

const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const parsePickupDate = (value) => {
  if (!value) return null;
  // Backend stores YYYY-MM-DD or full ISO string
  const parsed = typeof value === 'string' ? parseISO(value) : value;
  return isValid(parsed) ? parsed : null;
};

export default function OrdersCalendar({ orders }) {
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  // Group orders by yyyy-MM-dd, ignoring cancelled (they don't need preparing)
  const ordersByDay = useMemo(() => {
    const map = new Map();
    for (const order of orders) {
      if (order.status === 'cancelled') continue;
      const d = parsePickupDate(order.pickup_date);
      if (!d) continue;
      const key = format(d, 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(order);
    }
    // Sort each day's orders by pickup_time
    for (const list of map.values()) {
      list.sort((a, b) => (a.pickup_time || '').localeCompare(b.pickup_time || ''));
    }
    return map;
  }, [orders]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const today = new Date();
  const selectedDayKey = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null;
  const selectedDayOrders = selectedDayKey ? ordersByDay.get(selectedDayKey) || [] : [];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-stone-100">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={() => setCursor((c) => subMonths(c, 1))}
            data-testid="cal-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] capitalize min-w-[180px] text-center">
            {format(cursor, 'MMMM yyyy')}
          </h2>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={() => setCursor((c) => addMonths(c, 1))}
            data-testid="cal-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <Button
          variant="outline"
          className="rounded-full text-sm"
          onClick={() => {
            setCursor(new Date());
            setSelectedDay(new Date());
          }}
          data-testid="cal-today"
        >
          Hoje
        </Button>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 text-center text-xs uppercase tracking-wide text-stone-500 py-2 border-b border-stone-100">
        {dayNames.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const dayOrders = ordersByDay.get(key) || [];
          const isCurrentMonth = isSameMonth(day, cursor);
          const isToday = isSameDay(day, today);
          const isSelected = selectedDay && isSameDay(day, selectedDay);
          // Aggregate up to 3 status colours for dots
          const statusList = [...new Set(dayOrders.map((o) => o.status))].slice(0, 3);

          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelectedDay(day)}
              data-testid={`cal-day-${key}`}
              className={`relative min-h-[92px] border-r border-b border-stone-100 last:border-r-0 p-2 text-left flex flex-col gap-1 transition-colors ${
                isCurrentMonth ? 'bg-white' : 'bg-stone-50/60'
              } ${isSelected ? 'ring-2 ring-[#8D6E63] ring-inset' : ''} hover:bg-[#FFFBEF]`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-sm font-medium ${
                    isCurrentMonth ? 'text-stone-700' : 'text-stone-400'
                  } ${isToday ? 'bg-[#1a1a1a] text-white rounded-full w-6 h-6 inline-flex items-center justify-center' : ''}`}
                >
                  {format(day, 'd')}
                </span>
                {dayOrders.length > 0 && (
                  <span className="text-[10px] font-bold text-white bg-[#D4AF37] rounded-full px-1.5 py-0.5">
                    {dayOrders.length}
                  </span>
                )}
              </div>
              {/* Up to 2 customer name preview */}
              <div className="space-y-0.5 overflow-hidden">
                {dayOrders.slice(0, 2).map((o) => (
                  <div
                    key={o.id}
                    className="text-[11px] truncate text-stone-600 flex items-center gap-1"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_META[o.status]?.color || 'bg-stone-400'}`} />
                    <span className="truncate">
                      {o.pickup_time ? `${o.pickup_time} · ` : ''}
                      {o.contact_name || 'Cliente'}
                    </span>
                  </div>
                ))}
                {dayOrders.length > 2 && (
                  <div className="text-[10px] text-stone-400">+{dayOrders.length - 2} mais</div>
                )}
              </div>
              {/* status dots row (decorative) */}
              {statusList.length > 0 && (
                <div className="flex gap-1 mt-auto">
                  {statusList.map((s) => (
                    <span
                      key={s}
                      className={`w-1.5 h-1.5 rounded-full ${STATUS_META[s]?.color || 'bg-stone-300'}`}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected day panel */}
      {selectedDay && (
        <div className="border-t border-stone-100 p-5 bg-stone-50/50">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-[#8D6E63]" />
            <h3 className="font-semibold text-[#1a1a1a]">
              {format(selectedDay, "EEEE, d 'de' MMMM")} ·{' '}
              <span className="text-stone-500 font-normal">
                {selectedDayOrders.length} pedido{selectedDayOrders.length === 1 ? '' : 's'}
              </span>
            </h3>
          </div>

          {selectedDayOrders.length === 0 ? (
            <div className="text-center py-6 text-sm text-stone-500">
              <ShoppingBag className="w-8 h-8 text-stone-300 mx-auto mb-2" />
              Nenhum pedido para este dia.
            </div>
          ) : (
            <div className="space-y-2">
              {selectedDayOrders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white rounded-xl border border-stone-100 p-3 flex items-center justify-between gap-3"
                  data-testid={`cal-order-${order.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0 ${
                        STATUS_META[order.status]?.color || 'bg-stone-400'
                      }`}
                    >
                      <ShoppingBag className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#1a1a1a] truncate">
                          {order.contact_name || 'Cliente'}
                        </span>
                        <span className="text-xs text-stone-500">
                          {order.pickup_time}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-stone-500 mt-0.5">
                        {order.contact_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {order.contact_phone}
                          </span>
                        )}
                        {order.contact_email && (
                          <span className="flex items-center gap-1 truncate max-w-[220px]">
                            <Mail className="w-3 h-3" />
                            {order.contact_email}
                          </span>
                        )}
                        <span>
                          €{Number(order.total || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span
                    className={`text-xs px-3 py-1 rounded-full text-white shrink-0 ${
                      STATUS_META[order.status]?.color || 'bg-stone-400'
                    }`}
                  >
                    {STATUS_META[order.status]?.label || order.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
